import { Product } from './product';
import { Challenge, Outcome } from './customer';

export enum OpportunityPhase {
  Discovery = 'discovery',
  Present = 'present',
  Design = 'design',
  Validate = 'validate',
  TechClose = 'tech_close',
  TechCloseWon = 'tech_close_won',
  TechCloseLost = 'tech_close_lost',
}

export const OpportunityPhaseLabel: Record<OpportunityPhase, string> = {
  [OpportunityPhase.Discovery]: 'Discovery',
  [OpportunityPhase.Present]: 'Present',
  [OpportunityPhase.Design]: 'Design',
  [OpportunityPhase.Validate]: 'Validate',
  [OpportunityPhase.TechClose]: 'Technical Close',
  [OpportunityPhase.TechCloseWon]: 'Tech Close Won',
  [OpportunityPhase.TechCloseLost]: 'Tech Close Lost',
};

export enum OpportunitySEStatus {
  NoneAssigned = 'No SE Assigned',
  NotRequired = 'No SE Required',
  WorkInProgress = 'SE Work in Progress',
  Completed = 'SE Completed',
  TechnicalClose = 'Technical Close',
  TechnicalOnHold = 'Technical On-Hold',
  Unknown = 'Unknown SE Status',
}

export enum OpportunitySETag {
  TagSE = 'Tag',
  UntagSE = 'Untag',
}

export enum OpportunityUpdate {
  Status = 'status',
  Tag = 'tag',
  Note = 'note',
}

interface Attributes {
  type: string;
  url: string;
}

export interface Account {
  attributes: Attributes;
  Id: string;
  Name: string;
  Website: string | null;
}

export interface RecordType {
  attributes: Attributes;
  Id: string;
  Name: string;
}

export interface SFUser {
  attributes: Attributes;
  Id: string;
  Name: string;
}

export interface Amount {
  value: number | null;
  label: string;
}

export interface Quote {
  attributes: Attributes;
  Id: string;
  IsDeleted: boolean;
  Name: string;
  CurrencyIsoCode: string;
  RecordTypeId: string;
  CreatedDate: string;
  CreatedById: string;
  LastModifiedDate: string;
  LastModifiedById: string;
  SystemModstamp: string;
  LastActivityDate: string | null;
  LastViewedDate: string | null;
  LastReferencedDate: string | null;
}

export interface Quotes {
  totalSize: number;
  done: boolean;
  records: Quote[];
}

export interface Opportunity {
  id: string;
  attributes: Attributes | null;
  Account: Account;
  RecordType: RecordType;
  SE_Last_Modified_By__r: SFUser | null;
  Name: string | null;
  DealID__c: string | null;
  Amount: string | null;
  Forecast_Category__c: string | null;
  CloseDate: string | null;
  StageName: string | null;
  Cisco_Terr_6__c: string | null;
  Business_Entity_Type__c: string | null;
  AccountId: string | null;
  Account_Owner_ID__c: string | null;
  ACV__c: Amount | null;
  CCW_CCE_Quote__c: string | null;
  Competitors__c: string | null;
  Description: string | null;
  Expected_iACV__c: Amount | null;
  Expected_Renewed__c: Amount | null;
  Expected_Term__c: string | null;
  Expected_Service__c: Amount | null;
  Estimated_Opportunity_Amount__c: Amount | null;
  ForecastCategory: string | null;
  Hyper_Specialist_Notes__c: string | null;
  Overlay_Assigned__c: string | null;
  Is_Renewal_Oppty__c: boolean;
  isSEStatusUpdatedOnce__c: boolean;
  LastActivityDate: string | null;
  LastStageChangeDate: string;
  LID__LinkedIn_Company_Id__c: string | null;
  NextStep: string | null;
  Next_Steps_History__c: string | null;
  Notes: string | null;
  NotesAndAttachments: string | null;
  OpportunityCompetitors: string | null;
  CurrencyIsoCode: string | null;
  Opportunity_Quote_Count__c: number | null;
  Opportunity_Status__c: string | null;
  Partners: string | null;
  Probability: number | null;
  CCW_CCE_Quote_Id__c: string | null;
  Quote_Status__c: string | null;
  Quotes__r: Quotes | null;
  Cisco_Terr_1__c: string | null;
  Cisco_Terr_2__c: string | null;
  Cisco_Terr_3__c: string | null;
  Cisco_Terr_4__c: string | null;
  Cisco_Terr_5__c: string | null;
  SE_Last_Modified_By__c: string | null;
  SE_Last_Modified_Date__c: string | null;
  SE_Notes_New__c: string | null;
  SE_Notes__c: string | null;
  SE_Status__c: string| null;
  SE_Assigned__c: string| null;
  Security_Amount__c: Amount| null;
  Status__c: string | null;
  Cisco_Sub_Sales_Coverage__c: string | null;
  Discovery_Completed__c: boolean;
  Sync_with_CCW__c: boolean | null;
  Tag_Assignments__r: string | null;
  Tasks: string | null;
  Term_Months__c: string | null;
  Id?: string;
  ForecastCategoryName?: string | null;
  ExpectedRevenue?: string | null;
  LeadSourc?: string | null;
  OrderNumber__c?: string | null;
  LastModifiedDate?: string | null;
  isTagged: boolean | false;
  products?: Product[] | [];
  OpportunityProducts?: OpportunityProduct[] | [];
  customer_challenge?: string | null;
  organizational_priority?: string | null;
  solution_type?: string | null;
  init_config?: boolean | false;
  isChecked?: boolean | false;
  se_phase?: OpportunityPhase | null;
  ai_context?: string | null;
  last_updated?: Date | null;
  highest_se_phase?: OpportunityPhase | null;
  updated_by?: string | null;
  customer_id?: string | null;
}

export interface OpportunityProduct {
  id: number;
  product_id?: number;
  opportunity: string;
  product: Product;
  validation_method?: string;
  planned_start_date?: string;
  planned_end_date?: string;
  sem_approved?: boolean;
  pov_kickoff_date?: string;
  pov_end_date?: Date;
  sem_complete_approved?: boolean;
  duration?: number;
  last_updated?: Date;
  updated_by?: string;
  owner?: string;
  sem_approver?: string;
  sf_pov_activity_id?: string | null;
  sf_pov_activity_created?: boolean | null;
}

// -- ArchitectureComponent, Product, ProductVersion, ProductLicense, Challenge, Outcome, Customer, GoToMarket, User types assumed to be defined elsewhere --

export interface OpportunityArchitecture {
  id: number;
  opportunity: string;
  architecture: number;
  last_updated: string;
  updated_by: string;
}

export interface OpportunityChallenge {
  id: number;
  opportunity: Opportunity
  challenge: Challenge
  last_updated?: string | null;
  updated_by?: string | null;
}

export interface OpportunityOutcome {
  id: number;
  opportunity: Opportunity;
  outcome: Outcome;
  last_updated: string | null;
  updated_by: string | null;
}
